import React, { useMemo, useState } from 'react';

import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, IconButton, Stack, Typography,
} from '@mui/material';

import { alpha } from '@mui/material/styles';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';

import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';

import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';

import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined';

import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import DashboardChartCard from '../../dashboard/components/DashboardChartCard';

import { MONO_FONT, SCOPE_TOKENS, TOKENS } from '../../../theme/muiTheme';

import { CLIMATE_ACTION_COLORS } from '../model/climateActionColors';

import { scopeChipSx } from '../model/climateActionPresentation';

import { formatTonne } from '../../../shared/utils/number';

import { resolveUserFacingError } from '../../../shared/utils/actionFeedback';

import { deliveryStatusLabelKey } from '../model/actionStatusModel';

import RecommendationRunStatus from './RecommendationRunStatus';

import RecommendationToolbar, { DEFAULT_RECOMMENDATION_GROUPS } from './RecommendationToolbar';

import { formatRecommendationRunDate, RECOMMENDATION_RUN_STATUS } from './recommendationRunModel';

import ClimateActionKpis from './ClimateActionKpis';

import SecondaryImpactStrip from './CompensationReviewCard';

import TargetPathwayChart from './TargetPathwayChart';

import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/swiper.min.css';

const AI_GROUPS = DEFAULT_RECOMMENDATION_GROUPS;

const normalizeScope = (value) => {
  const scope = String(value || '').replace(/[\s_-]/g, '').toLowerCase();
  if (scope.startsWith('scope1')) return 'scope1';
  if (scope.startsWith('scope2')) return 'scope2';
  if (scope.startsWith('scope3')) return 'scope3';
  return 'other';
};

const scopeTone = (scope) => {
  const normalized = normalizeScope(scope);
  if (normalized === 'scope1') return SCOPE_TOKENS.SCOPE_1.color;
  if (normalized === 'scope2') return SCOPE_TOKENS.SCOPE_2.color;
  if (normalized === 'scope3') return SCOPE_TOKENS.SCOPE_3.color;
  return CLIMATE_ACTION_COLORS.other;
};

const actionStatusChipColor = status => {
  if (status === 'COMPLETED') return 'success';
  if (status === 'IN_PROGRESS') return 'warning';
  if (status === 'NOT_APPROVED' || status === 'REJECTED') return 'error';
  return 'default';
};

const localizedScopeLabel = (action, t) => {
  if (action.accountingClassification === 'CARBON_CREDIT') {
    return t('climateAction.actions.carbonCreditLabel', { defaultValue: 'Carbon credit' });
  }
  const scope = action.scope || 'OTHER';
  if (scope === 'OTHER' || scope === 'NOT_APPLICABLE') {
    return t('climateAction.actions.otherLabel', { defaultValue: 'Other' });
  }
  return t(`climateAction.scope.${normalizeScope(scope)}`, {
    defaultValue: scope,
  });
};

const boundaryNameFor = recommendation => (
  recommendation.boundary?.name || recommendation.boundary?.ref_id || null
);

const matchesContext = (recommendation, context) => {
  if (!context) return true;
  if (normalizeScope(recommendation.scope) !== context.scope) return false;
  const expectedBoundary = (context.boundaryId || context.boundaryName || '').toString().toLowerCase();
  if (expectedBoundary) {
    const boundaryValues = [
      recommendation.boundary?.name,
      recommendation.boundary?.ref_id,
    ].filter(Boolean).map(value => String(value).toLowerCase());
    return boundaryValues.includes(expectedBoundary);
  }
  const expectedCategory = (context.category || '').toString().toLowerCase();
  if (expectedCategory) {
    return String(recommendation.category || '').toLowerCase() === expectedCategory;
  }
  return true;
};

const parseSourceKeyParts = (sourceKey) => {
  const parts = String(sourceKey || '').split('|');
  return {
    scope: parts[0] || '',
    category: parts[1] || '',
    subCategory: parts[2] || '',
    boundaryRef: parts[3] || '',
  };
};

function SectionHeading({ action, color, icon: Icon, title }) {
  const displayColor = theme => theme.palette.mode === 'dark' && color === CLIMATE_ACTION_COLORS.ai
    ? CLIMATE_ACTION_COLORS.aiDark : color;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{
          alignItems: 'center',
          bgcolor: theme => alpha(displayColor(theme), 0.11),
          borderRadius: 1.5,
          color: displayColor,
          display: 'flex',
          height: 34,
          justifyContent: 'center',
          width: 34,
        }}>
          <Icon sx={{ fontSize: 21 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>{title}</Typography>
        </Box>
      </Stack>
      <Box sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}>
        {action}
      </Box>
    </Stack>
  );
}

/** One Swiper-powered carousel of action cards, capped at twenty (P5). */
function CurrentActionsCarousel({ actions, t, year }) {
  const [swiper, setSwiper] = useState(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(actions.length <= 1);

  const syncNavigation = instance => {
    setIsBeginning(instance.isBeginning);
    setIsEnd(instance.isEnd);
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ mb: 0.75 }}>
        <IconButton
          aria-label={t('climateAction.actions.previous', { defaultValue: 'Previous actions' })}
          disabled={!swiper || isBeginning}
          onClick={() => swiper?.slidePrev()}
          size="small"
        >
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label={t('climateAction.actions.next', { defaultValue: 'Next actions' })}
          disabled={!swiper || isEnd}
          onClick={() => swiper?.slideNext()}
          size="small"
        >
          <ArrowForwardIosRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Swiper
        allowTouchMove
        grabCursor
        onInit={instance => {
          setSwiper(instance);
          syncNavigation(instance);
        }}
        onResize={syncNavigation}
        onSlideChange={syncNavigation}
        slidesPerView="auto"
        spaceBetween={8}
        style={{ paddingBottom: '4px' }}
      >
        {actions.map(action => {
          const actionTone = scopeTone(action.scope);
          return (
            <SwiperSlide
              key={action.actionId}
              style={{ width: 'min(230px, 82vw)' }}
            >
              <Card component="article" sx={{
                bgcolor: 'background.paper',
                borderColor: 'divider',
                borderRadius: 1.5,
                boxShadow: 'none',
                boxSizing: 'border-box',
                height: 126,
                p: 1.35,
              }} variant="outlined">
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography sx={{ color: actionTone, fontSize: 11.5, fontWeight: 650 }}>
                      {localizedScopeLabel(action, t)}
                    </Typography>
                    <Chip
                      color={actionStatusChipColor(action.status)}
                      label={t(deliveryStatusLabelKey(action.status))}
                      size="small"
                      sx={{ height: 22, '& .MuiChip-label': { fontSize: 11, px: 0.8 } }}
                      variant="outlined"
                    />
                  </Stack>
                  <Typography sx={{
                    display: '-webkit-box',
                    fontSize: 14.5,
                    fontWeight: 650,
                    lineHeight: 1.3,
                    minHeight: 38,
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                  }}>
                    {action.title}
                  </Typography>
                  <Typography sx={{
                    color: actionTone,
                    fontFamily: MONO_FONT,
                    fontSize: 15,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}>
                    {formatTonne(action.verifiedTco2e, { withUnit: true })}
                  </Typography>
                </Stack>
              </Card>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {actions.length === 0 && (
        <Alert severity="info">
          {t('climateAction.actions.emptyForYear', { year })}
        </Alert>
      )}
    </Box>
  );
}

/** One AI recommendation card with save/dismiss/view actions (P7). */
function RecommendationListItem({
  canMutate, onDismiss, onSave, onView, recommendation, saving, t,
}) {
  const scope = normalizeScope(recommendation.scope);
  const boundaryName = boundaryNameFor(recommendation);
  const needsData = recommendation.estimate_status === 'NEEDS_DATA';

  return (
    <Card
      component="article"
      sx={{
        bgcolor: 'background.paper',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        p: 1.75,
      }}
      variant="outlined"
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography sx={{ color: TOKENS.success, fontSize: 12, fontWeight: 700 }}>
            {t('climateAction.ai.topRank', { rank: recommendation.rank })}
          </Typography>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            <Chip
              label={t(`climateAction.scope.${scope}`, { defaultValue: recommendation.scope })}
              size="small"
              sx={scopeChipSx(scope)}
            />
            {recommendation.category && (
              <Chip
                label={t(`climateAction.category.${recommendation.category}`, { defaultValue: recommendation.category })}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
        </Stack>

        <Typography component="h3" sx={{ fontSize: 15.5, fontWeight: 700, lineHeight: 1.32 }}>
          {recommendation.solution_name}
        </Typography>

        {boundaryName && recommendation.boundary?.type !== 'COMPANY' && (
          <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
            {boundaryName}
          </Typography>
        )}

        {recommendation.top_offices?.length > 0 && (
          <Typography color="text.secondary" sx={{ fontSize: 12.5, lineHeight: 1.45 }}>
            {t('climateAction.ai.topOffices', { defaultValue: 'Top offices' })}:{' '}
            {recommendation.top_offices
              .map(office => `${office.name} ${formatTonne(office.emissions_tco2e, { withUnit: true })}`)
              .join(' · ')}
          </Typography>
        )}

        <Box sx={{
          bgcolor: theme => alpha(TOKENS.success, theme.palette.mode === 'dark' ? 0.12 : 0.06),
          border: 1,
          borderColor: theme => alpha(TOKENS.success, theme.palette.mode === 'dark' ? 0.35 : 0.2),
          borderRadius: 1,
          px: 1.2,
          py: 1,
        }}>
          <Stack spacing={0.65}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
              <Typography color="text.primary" sx={{ fontSize: 12.5, fontWeight: 650 }}>
                {t('climateAction.ai.sourceEmissions', { defaultValue: 'Source emissions' })}
              </Typography>
              <Typography sx={{ fontFamily: MONO_FONT, fontSize: 14, fontWeight: 700 }}>
                {formatTonne(recommendation.source_emissions_tco2e, { withUnit: true })}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
              <Typography color="text.primary" sx={{ fontSize: 12.5, fontWeight: 650 }}>
                {t('climateAction.ai.estimatedReduction', { defaultValue: 'Estimated reduction' })}
              </Typography>
              {needsData ? (
                <Chip
                  color="warning"
                  label={t('climateAction.ai.needsDataLabel', { defaultValue: 'Needs data' })}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, '& .MuiChip-label': { fontSize: 11, px: 0.8 } }}
                />
              ) : (
                <Typography sx={{ color: TOKENS.success, fontFamily: MONO_FONT, fontSize: 14, fontWeight: 700 }}>
                  {recommendation.reduction_percent !== null && recommendation.reduction_percent !== undefined
                    ? `−${recommendation.reduction_percent}% · ${formatTonne(recommendation.estimated_tco2e, { withUnit: true })}/yr`
                    : t('climateAction.ai.cannotCalculate')}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ mt: 'auto !important', pt: 0.75 }}>
          <Divider sx={{ mb: 1.1 }} />
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
            <Button
              onClick={() => onView(recommendation)}
              size="small"
              startIcon={<VisibilityOutlinedIcon />}
              variant="outlined"
            >
              {t('climateAction.ai.viewDetails')}
            </Button>
            {canMutate && (
              <Button
                disabled={saving}
                onClick={() => onSave(recommendation)}
                size="small"
                startIcon={<BookmarkAddOutlinedIcon />}
                variant="contained"
              >
                {t('climateAction.ai.saveAsDraft')}
              </Button>
            )}
            {canMutate && (
              <Button
                color="inherit"
                onClick={() => onDismiss(recommendation)}
                size="small"
                startIcon={<DoNotDisturbAltOutlinedIcon />}
              >
                {t('climateAction.ai.notRelevant')}
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
}

/** Full detail of one recommendation (doc 07 §10.1). */
function AiRecommendationDetailDialog({ onClose, open, recommendation, t }) {
  if (!recommendation) return null;
  const scope = normalizeScope(recommendation.scope);
  const boundaryName = boundaryNameFor(recommendation);
  const needsData = recommendation.estimate_status === 'NEEDS_DATA';

  return (
    <Dialog aria-labelledby="ai-recommendation-detail-title" maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle id="ai-recommendation-detail-title" sx={{ pr: 6 }}>
        {t('climateAction.ai.viewDetails')}
      </DialogTitle>
      <IconButton aria-label={t('common.close')} onClick={onClose} sx={{ position: 'absolute', right: 14, top: 14 }}>
        <CloseRoundedIcon />
      </IconButton>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography component="h3" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.32 }}>
              {recommendation.solution_name}
            </Typography>
            <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip label={t('climateAction.ai.topRank', { rank: recommendation.rank })} size="small" variant="outlined" />
              <Chip
                label={t(`climateAction.scope.${scope}`, { defaultValue: recommendation.scope })}
                size="small"
                sx={scopeChipSx(scope)}
              />
              {recommendation.category && (
                <Chip
                  label={t(`climateAction.category.${recommendation.category}`, { defaultValue: recommendation.category })}
                  size="small"
                  variant="outlined"
                />
              )}
              {boundaryName && <Chip label={boundaryName} size="small" variant="outlined" />}
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {t('climateAction.ai.why')}
            </Typography>
            <Typography sx={{ fontSize: 14, lineHeight: 1.6, mt: 0.5 }}>
              {recommendation.rationale || t('climateAction.ai.notAvailable', { defaultValue: 'Not available' })}
            </Typography>
          </Box>

          {recommendation.top_offices?.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {t('climateAction.ai.topOffices', { defaultValue: 'Top offices' })}
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: 1.6, mt: 0.5 }}>
                {recommendation.top_offices
                  .map(office => `${office.name} · ${formatTonne(office.emissions_tco2e, { withUnit: true })}`)
                  .join('\n')}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {t('climateAction.ai.expectedBenefit', { defaultValue: 'Expected benefit' })}
            </Typography>
            <Typography sx={{ fontSize: 14, lineHeight: 1.6, mt: 0.5 }}>
              {recommendation.benefit || recommendation.rationale || t('climateAction.ai.notAvailable', { defaultValue: 'Not available' })}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {t('climateAction.ai.currentEmissionBasis', { defaultValue: 'Current emission basis' })}
            </Typography>
            <Typography sx={{ fontFamily: MONO_FONT, fontSize: 15, fontWeight: 700, mt: 0.5 }}>
              {formatTonne(recommendation.source_emissions_tco2e, { withUnit: true })}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {t('climateAction.ai.estimatedReduction', { defaultValue: 'Estimated reduction' })}
            </Typography>
            {needsData ? (
              <Chip
                color="warning"
                label={t('climateAction.ai.needsDataLabel', { defaultValue: 'Needs data' })}
                size="small"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography sx={{ color: TOKENS.success, fontFamily: MONO_FONT, fontSize: 15, fontWeight: 700, mt: 0.5 }}>
                {recommendation.reduction_percent !== null && recommendation.reduction_percent !== undefined
                  ? `−${recommendation.reduction_percent}% · ${formatTonne(recommendation.estimated_tco2e, { withUnit: true })}/yr`
                  : t('climateAction.ai.cannotCalculate')}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Row of dismissed sources with an undo action for each (doc 07 §6.3). */
function DismissedSourcesRow({ dismissedSources, onUndoDismiss, t }) {
  if (!dismissedSources || dismissedSources.length === 0) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography color="text.secondary" sx={{ fontSize: 12.5, fontWeight: 650 }}>
        {t('climateAction.ai.dismissedSources', { count: dismissedSources.length })}
      </Typography>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
        {dismissedSources.map((item) => {
          const parts = parseSourceKeyParts(item.source_key);
          const label = [parts.scope, parts.category, parts.subCategory, parts.boundaryRef]
            .filter(Boolean).join(' · ');
          return (
            <Chip
              key={item.source_key}
              label={label || item.source_key}
              onDelete={() => onUndoDismiss(item.source_key)}
              size="small"
              variant="outlined"
            />
          );
        })}
      </Stack>
    </Box>
  );
}

/** Compose the FY-aware Climate Action dashboard from the real endpoint (P2+P5). */
export default function ClimateActionDashboard({
  actionImpact = {}, actions = [], aiRun = null, aiRunGenerating = false,
  canMutate, carbonCredit = {}, context = null, showAiRecommendations = true,
  locale = 'en', onClearContext, onDismissSource, onGenerate, onSaveAsDraft,
  onToast, onUndoDismissSource, onViewActionPlan,
  otherClimateImpact = {}, pathway, position, selectedYear, t,
}) {
  const sortedActions = useMemo(() => [...actions], [actions]);
  const [recommendationGroupFilter, setRecommendationGroupFilter] = useState('all');
  const [recommendationSearch, setRecommendationSearch] = useState('');
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  const [detailRecommendation, setDetailRecommendation] = useState(null);
  const [savingRecommendationId, setSavingRecommendationId] = useState(null);

  const recommendations = useMemo(() => aiRun?.recommendations || [], [aiRun]);
  const dismissedSources = useMemo(() => aiRun?.dismissed_sources || [], [aiRun]);
  const hasRun = Boolean(aiRun?.generated_at);
  const runStatus = aiRunGenerating
    ? RECOMMENDATION_RUN_STATUS.UPDATING
    : hasRun ? RECOMMENDATION_RUN_STATUS.READY : RECOMMENDATION_RUN_STATUS.NO_RUN;

  const openGenerateConfirm = () => {
    if (!canMutate || aiRunGenerating) return;
    setConfirmGenerateOpen(true);
  };

  const confirmGenerate = async () => {
    setConfirmGenerateOpen(false);
    try {
      await onGenerate();
      onToast?.({ severity: 'success', message: t('climateAction.ai.generateSuccess') });
    } catch (error) {
      onToast?.({ severity: 'error', message: resolveUserFacingError(error, t('climateAction.ai.generateFailed')) });
    }
  };

  const saveRecommendation = async (recommendation) => {
    setSavingRecommendationId(recommendation.rank);
    try {
      await onSaveAsDraft(recommendation);
      onToast?.({ severity: 'success', message: t('climateAction.ai.savedAsDraft') });
    } catch (error) {
      onToast?.({ severity: 'error', message: resolveUserFacingError(error, t('climateAction.ai.saveFailed')) });
    } finally {
      setSavingRecommendationId(null);
    }
  };

  const dismissRecommendation = async (recommendation) => {
    try {
      await onDismissSource(recommendation.source_key);
      onToast?.({ severity: 'success', message: t('climateAction.ai.dismissed') });
    } catch (error) {
      onToast?.({ severity: 'error', message: resolveUserFacingError(error, t('climateAction.ai.dismissFailed')) });
    }
  };

  const undoDismiss = async (sourceKey) => {
    try {
      await onUndoDismissSource(sourceKey);
      onToast?.({ severity: 'success', message: t('climateAction.ai.dismissUndone') });
    } catch (error) {
      onToast?.({ severity: 'error', message: resolveUserFacingError(error, t('climateAction.ai.dismissFailed')) });
    }
  };

  const filteredRecommendations = useMemo(() => {
    const query = recommendationSearch.trim().toLowerCase();
    const dismissedKeys = new Set(dismissedSources.map(item => item.source_key));
    return recommendations.filter((recommendation) => {
      if (recommendation.already_saved) return false;
      if (dismissedKeys.has(recommendation.source_key)) return false;
      if (!matchesContext(recommendation, context)) return false;
      const scopeMatch = recommendationGroupFilter === 'all'
        || normalizeScope(recommendation.scope) === recommendationGroupFilter;
      const searchMatch = !query
        || String(recommendation.solution_name || '').toLowerCase().includes(query)
        || String(recommendation.rationale || '').toLowerCase().includes(query);
      return scopeMatch && searchMatch;
    });
  }, [recommendations, dismissedSources, context, recommendationGroupFilter, recommendationSearch]);

  const contextChips = useMemo(() => {
    if (!context) return [];
    const chips = [];
    if (context.boundaryId || context.boundaryName) {
      chips.push({
        id: 'boundary',
        label: t('climateAction.ai.contextBanner', {
          boundary: context.boundaryName || context.boundaryId,
          year: context.year,
        }),
        clearable: false,
      });
    } else if (context.category) {
      chips.push({
        id: 'category',
        label: t('climateAction.ai.categoryContextBanner', {
          category: context.category,
          year: context.year,
        }),
        clearable: false,
      });
    } else {
      chips.push({
        id: 'scope',
        label: t('climateAction.ai.scopeHeading', {
          scope: t(`climateAction.scope.${context.scope}`, { defaultValue: context.scope }),
          year: context.year,
        }),
        clearable: false,
      });
    }
    chips.push({ id: 'clear', label: t('climateAction.ai.clearContext') });
    return chips;
  }, [context, t]);

  return (
    <Stack spacing={2}>
      <ClimateActionKpis
        impactSummary={actionImpact}
        otherClimateImpact={otherClimateImpact}
        position={position}
        selectedYear={selectedYear}
        t={t}
      />

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'minmax(0, 1fr)' }}>
        <Box sx={{ display: 'flex', minWidth: 0 }}>
          <DashboardChartCard
            icon={ShowChartOutlinedIcon}
            title={t('climateAction.pathway.title')}
            unit={t('dashboard.chart.unit')}
            sx={{
              bgcolor: 'background.paper',
              borderColor: 'divider',
              borderRadius: 1.5,
              boxShadow: theme => (theme.palette.mode === 'dark'
                ? 'none'
                : '0 10px 30px rgba(24, 39, 51, 0.045)'),
              width: '100%',
            }}
          >
            <TargetPathwayChart
              isHistoricalTarget={position?.isHistoricalTarget}
              pathway={pathway}
              selectedYear={selectedYear}
              t={t}
            />
          </DashboardChartCard>
        </Box>
      </Box>

      <SecondaryImpactStrip
        carbonCredit={carbonCredit}
        otherClimateImpact={otherClimateImpact}
        t={t}
      />

      <Card component="section" sx={{
        bgcolor: 'background.paper',
        borderColor: 'divider',
        borderRadius: 1.5,
        boxShadow: theme => (theme.palette.mode === 'dark'
          ? 'none'
          : '0 10px 30px rgba(24, 39, 51, 0.045)'),
      }}>
        <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
          <SectionHeading
            action={canMutate && (
              <Button fullWidth variant="outlined" onClick={() => onViewActionPlan('all')}>
                {t('climateAction.actions.managePlan')}
              </Button>
            )}
            color={TOKENS.teal}
            icon={ChecklistOutlinedIcon}
            title={t('climateAction.actions.currentTitle')}
          />
          <CurrentActionsCarousel
            actions={sortedActions}
            t={t}
            year={selectedYear}
          />
        </CardContent>
      </Card>

      {showAiRecommendations && <Card component="section" id="ai-green-recommendations" sx={{
        bgcolor: 'background.paper',
        borderColor: 'divider',
        borderRadius: 1.5,
        boxShadow: theme => (theme.palette.mode === 'dark'
          ? 'none'
          : '0 10px 30px rgba(24, 39, 51, 0.045)'),
      }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <SectionHeading
            action={(
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
                <RecommendationRunStatus
                  canMutate={canMutate}
                  onRun={openGenerateConfirm}
                  run={{ status: runStatus }}
                  t={t}
                />
              </Stack>
            )}
            color={CLIMATE_ACTION_COLORS.ai}
            icon={AutoAwesomeOutlinedIcon}
            title={t('climateAction.ai.title')}
          />

          {hasRun && (
            <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 1 }}>
              {t('climateAction.ai.runMetadata', {
                year: selectedYear,
                datetime: formatRecommendationRunDate(aiRun.generated_at, locale),
              })}
              {' · '}
              {t('climateAction.ai.runsRemaining', { count: aiRun.runs_remaining_today ?? 0 })}
            </Typography>
          )}

          <RecommendationToolbar
            contextChips={contextChips}
            groups={AI_GROUPS}
            onClearContext={onClearContext}
            onGroupChange={setRecommendationGroupFilter}
            onSearchChange={setRecommendationSearch}
            searchValue={recommendationSearch}
            selectedGroup={recommendationGroupFilter}
            t={t}
          />

          {!hasRun && !aiRunGenerating && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('climateAction.ai.empty', {
                defaultValue: 'AI recommendations will appear here once the analysis run is available.',
              })}
            </Alert>
          )}

          {aiRunGenerating && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('climateAction.ai.processing', {
                defaultValue: 'Analyzing the largest emission sources...',
              })}
            </Alert>
          )}

          {hasRun && filteredRecommendations.length > 0 && (
            <Box sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
              mt: 2,
            }}>
              {filteredRecommendations.map(recommendation => (
                <RecommendationListItem
                  canMutate={canMutate}
                  key={`${recommendation.rank}-${recommendation.solution_name}`}
                  onDismiss={dismissRecommendation}
                  onSave={saveRecommendation}
                  onView={setDetailRecommendation}
                  recommendation={recommendation}
                  saving={savingRecommendationId === recommendation.rank}
                  t={t}
                />
              ))}
            </Box>
          )}

          {hasRun && filteredRecommendations.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {context
                ? t('climateAction.ai.contextEmpty', {
                  defaultValue: 'No recommendation matches this exact boundary and fiscal year.',
                })
                : dismissedSources.length > 0
                  ? t('climateAction.ai.allDismissed', {
                    defaultValue: 'All recommendations have been dismissed. Undo a dismissal above or generate a new run.',
                  })
                  : t('climateAction.ai.empty', {
                    defaultValue: 'AI recommendations will appear here once the analysis run is available.',
                  })}
            </Alert>
          )}

          <DismissedSourcesRow
            dismissedSources={dismissedSources}
            onUndoDismiss={undoDismiss}
            t={t}
          />

          <AiRecommendationDetailDialog
            onClose={() => setDetailRecommendation(null)}
            open={Boolean(detailRecommendation)}
            recommendation={detailRecommendation}
            t={t}
          />

          <Dialog
            aria-labelledby="ai-generate-confirm-title"
            maxWidth="sm"
            onClose={() => setConfirmGenerateOpen(false)}
            open={confirmGenerateOpen}
          >
            <DialogTitle id="ai-generate-confirm-title">
              {t(hasRun ? 'climateAction.ai.regenerateConfirmTitle' : 'climateAction.ai.generateConfirmTitle')}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {t(hasRun ? 'climateAction.ai.regenerateConfirmMessage' : 'climateAction.ai.generateConfirmMessage', {
                  count: aiRun?.runs_remaining_today ?? 0,
                  year: selectedYear,
                })}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button color="inherit" onClick={() => setConfirmGenerateOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button autoFocus onClick={confirmGenerate} startIcon={<AutoAwesomeOutlinedIcon />} variant="contained">
                {t('climateAction.ai.confirmGenerate')}
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>}
    </Stack>
  );
}